// helpers/checks.js

import { safeStr, parsePiMultiple, normRat, ratEq } from "./piRats";

/* =========================
   Checking utilities
   ========================= */
export function normalizeEqLike(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/[−–—]/g, "-")
    .replace(/\\cdot/g, "*")
    .replace(/·/g, "*")
    .replace(/\\pi/g, "pi")
    .replace(/π/g, "pi")
    .replace(/\\left/g, "")
    .replace(/\\right/g, "")
    .replace(/\\frac\{([^{}]+)\}\{([^{}]+)\}/g, "($1)/($2)");
}

function cosArg(coef) {
  if (coef === 1) return "cosx";
  return `cos(${coef}x)`;
}

function sinArg(coef) {
  if (coef === 1) return "sinx";
  return `sin(${coef}x)`;
}

function cosHalfArg(coef) {
  if (coef % 2 === 0) return [cosArg(coef / 2)];
  return [`cos((${coef}x)/(2))`, `cos(${coef}x/2)`];
}

function asArray(v) {
  return Array.isArray(v) ? v : [v];
}

function includesAny(e, candidates) {
  return asArray(candidates).some((c) => e.includes(c));
}

function unorderedSumCandidates(a, b) {
  return [`${a}+${b}=0`, `${b}+${a}=0`];
}

function productCandidates(factors) {
  const variants = [];
  const joiners = ["*", ""];
  const multipliers = ["", "2*", "2"];
  for (const mult of multipliers) {
    for (const joiner of joiners) {
      variants.push(`${mult}${factors.join(joiner)}=0`);
    }
  }
  return variants;
}

function parsePiAffineFamily(raw) {
  const s = normalizeEqLike(raw)
    .replace(/^x=/, "")
    .replace(/^x\\in\\{/, "")
    .replace(/:k\\in.*$/, "")
    .replace(/,$/, "");
  const kIdx = s.indexOf("k");
  if (kIdx < 0) return null;

  const before = s.slice(0, kIdx);
  let after = s.slice(kIdx + 1);
  if (after.startsWith("*")) after = after.slice(1);
  if (!after) return null;

  let aRaw = before;
  let sign = "+";
  if (before.endsWith("+")) {
    aRaw = before.slice(0, -1);
  } else if (before.endsWith("-")) {
    aRaw = before.slice(0, -1);
    sign = "-";
  } else if (!before) {
    aRaw = "0";
  }

  const bRaw = `${sign === "-" ? "-" : ""}${after}`;
  const a = parsePiMultiple(aRaw || "0");
  const b = parsePiMultiple(bRaw);
  if (!a || !b) return null;
  return { a: normRat(a.num, a.den), b: normRat(b.num, b.den) };
}

/* =========================
   Step2 acceptance:
   - base pair: cos(mx)=0 and sum equation
   - split equations: cos(mx)=0 plus both simplified factors
   - product forms from either stage are also accepted
   - and NO extra wrong equations
   ========================= */
export function step2Check(eqsNorm, cfg) {
  if (cfg.kind === "cos") {
    const cosMx = cosArg(cfg.m);
    const cosX = "cosx";
    const splitA = cosHalfArg(cfg.m + 1).map((v) => `${v}=0`);
    const splitB = cosHalfArg(cfg.m - 1).map((v) => `${v}=0`);
    const sumCandidates = unorderedSumCandidates(cosX, cosMx);
    const productBaseCandidates = productCandidates([cosMx, `(${cosX}+${cosMx})`]).concat(
      productCandidates([cosMx, `(${cosMx}+${cosX})`])
    );
    const productSplitCandidates = cosHalfArg(cfg.m + 1).flatMap((a) =>
      cosHalfArg(cfg.m - 1).flatMap((b) => productCandidates([cosMx, a, b]))
    );

    const hasCosMx = eqsNorm.some((e) => e.includes(`${cosMx}=0`));
    const hasSum = eqsNorm.some((e) => includesAny(e, sumCandidates));
    const hasSplitA = eqsNorm.some((e) => includesAny(e, splitA));
    const hasSplitB = eqsNorm.some((e) => includesAny(e, splitB));
    const hasBaseProduct = eqsNorm.some((e) => includesAny(e, productBaseCandidates));
    const hasSplitProduct = eqsNorm.some((e) => includesAny(e, productSplitCandidates));

    const allowed = (e) =>
      e.includes(`${cosMx}=0`) ||
      includesAny(e, sumCandidates) ||
      includesAny(e, splitA) ||
      includesAny(e, splitB) ||
      includesAny(e, productBaseCandidates) ||
      includesAny(e, productSplitCandidates);

    const hasWrong = eqsNorm.some((e) => !allowed(e));
    if (hasWrong) return false;

    return (
      hasBaseProduct ||
      hasSplitProduct ||
      (hasCosMx && hasSum) ||
      (hasCosMx && hasSplitA && hasSplitB)
    );
  }

  if (cfg.kind === "sin") {
    const sinMx = sinArg(cfg.m);
    const cosM1 = cosArg(cfg.m - 1);
    const cosMx = cosArg(cfg.m);
    const splitA = cosHalfArg(2 * cfg.m - 1).map((v) => `${v}=0`);
    const splitB = ["cos(x/2)=0", "cos((x)/(2))=0"];
    const sumCandidates = unorderedSumCandidates(cosM1, cosMx);
    const productBaseCandidates = productCandidates([sinMx, `(${cosM1}+${cosMx})`]).concat(
      productCandidates([sinMx, `(${cosMx}+${cosM1})`])
    );
    const productSplitCandidates = cosHalfArg(2 * cfg.m - 1).flatMap((a) =>
      productCandidates([sinMx, a, "cos(x/2)"])
    );

    const hasSinMx = eqsNorm.some((e) => e.includes(`${sinMx}=0`));
    const hasSum = eqsNorm.some((e) => includesAny(e, sumCandidates));
    const hasSplitA = eqsNorm.some((e) => includesAny(e, splitA));
    const hasSplitB = eqsNorm.some((e) => includesAny(e, splitB));
    const hasBaseProduct = eqsNorm.some((e) => includesAny(e, productBaseCandidates));
    const hasSplitProduct = eqsNorm.some((e) => includesAny(e, productSplitCandidates));

    const allowed = (e) =>
      e.includes(`${sinMx}=0`) ||
      includesAny(e, sumCandidates) ||
      includesAny(e, splitA) ||
      includesAny(e, splitB) ||
      includesAny(e, productBaseCandidates) ||
      includesAny(e, productSplitCandidates);

    const hasWrong = eqsNorm.some((e) => !allowed(e));
    if (hasWrong) return false;

    return (
      hasBaseProduct ||
      hasSplitProduct ||
      (hasSinMx && hasSum) ||
      (hasSinMx && hasSplitA && hasSplitB)
    );
  }

  const baseA = normalizeEqLike(cfg.eq2_base[0]);
  const baseB = normalizeEqLike(cfg.eq2_base[1]);
  const split1 = normalizeEqLike(cfg.eq2_split[0]);
  const split2 = normalizeEqLike(cfg.eq2_split[1]);

  const hasBaseA = eqsNorm.some((e) => e.includes(baseA));

  const hasBaseB =
    eqsNorm.some((e) => e.includes(baseB)) ||
    (baseB.includes("+") &&
      (() => {
        const b = baseB.replace("=0", "");
        const parts = b.split("+");
        if (parts.length !== 2) return false;
        const rev = `${parts[1]}+${parts[0]}=0`;
        return eqsNorm.some((e) => e.includes(rev));
      })());

  const hasSplit1 = eqsNorm.some((e) => e.includes(split1));
  const hasSplit2 = eqsNorm.some((e) => e.includes(split2));

  const allowed = (e) =>
    e.includes(baseA) ||
    e.includes(baseB) ||
    e.includes(split1) ||
    e.includes(split2) ||
    (baseB.includes("+") &&
      (() => {
        const b = baseB.replace("=0", "");
        const parts = b.split("+");
        if (parts.length !== 2) return false;
        const rev = `${parts[1]}+${parts[0]}=0`;
        return e.includes(rev);
      })());

  const hasWrong = eqsNorm.some((e) => !allowed(e));

  const okBase = hasBaseA && hasBaseB && !hasWrong;
  const okSplit = hasBaseA && hasSplit1 && hasSplit2 && !hasWrong;

  return okBase || okSplit;
}

/* =========================
   Step3 acceptance: families x = a + k*b (both as pi-multiples)
   ========================= */
export function famObjToRats(famObj) {
  if (!famObj || typeof famObj !== "object") return null;
  if ("raw" in famObj) {
    const aff = parsePiAffineFamily(safeStr(famObj));
    if (aff) return aff;
  }
  if (famObj.affine && typeof famObj.affine === "object") {
    const a = parsePiMultiple(safeStr(famObj.affine.aRaw));
    const b = parsePiMultiple(safeStr(famObj.affine.bRaw));
    if (a && b) return { a: normRat(a.num, a.den), b: normRat(b.num, b.den) };
  }
  const a = parsePiMultiple(safeStr(famObj.a));
  const b = parsePiMultiple(safeStr(famObj.b));
  if (!a || !b) return null;
  return { a: normRat(a.num, a.den), b: normRat(b.num, b.den) };
}

export function isAllowedFamObj(famObj, cfg) {
  const rr = famObjToRats(famObj);
  if (!rr) return false;

  return (cfg.acceptedFamilies || cfg.families).some((p) => sameFamily(rr, p));
}

function sameFamily(user, expected) {
  if (!ratEq(user.b, expected.b)) return false;
  const diff = normRat(
    user.a.num * expected.a.den - expected.a.num * user.a.den,
    user.a.den * expected.a.den
  );
  if (!diff || diff.num === 0) return true;
  const quotient = normRat(diff.num * user.b.den, diff.den * user.b.num);
  return quotient && quotient.den === 1;
}
